<template>
    <div class="modal fade" ref="pricingTableModal" tabindex="-1" aria-labelledby="pricingTableModalLabel"
        aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
        <div class="blur-backdrop"></div>
        <div class="modal-dialog modal-custom-width modal-dialog-scrollable">
            <div class="modal-content" style="max-height: 80vh;">
                <div class="modal-header pricing-modal-header">
                    <h5 class="modal-title" id="pricingTableModalLabel">
                        Pricing Table Settings
                        <span class="product-count-badge ms-2">
                            <i class="fa-solid fa-cube"></i>
                            {{ activeParentProductCount }} {{ activeParentProductCount === 1 ? 'Product' : 'Products' }}
                        </span>
                    </h5>
                    <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                </div>
                <div class="modal-body">
                    <SimpleLoader v-if="isLoading"></SimpleLoader>
                    <div class="two-column-layout" v-if="!isLoading">
                        <div class="left-column">
                            <ul class="nav nav-tabs">
                                <li class="nav-item">
                                    <a class="nav-link" :class="{ active: activeTabIndex === 0 }"
                                        @click="activeTabIndex = 0"><strong>Option A</strong></a>
                                </li>
                                <li class="nav-item" v-for="(option, index) in productListOptions" :key="index">
                                    <a class="nav-link" :class="{ active: activeTabIndex === index + 1 }"
                                        @click="activeTabIndex = index + 1">
                                        <strong>Option {{ toOptionName(index + 2) }}</strong>
                                        <span class="remove-tab-icon" @click.stop="removeOption(option)">&times;</span>
                                    </a>
                                </li>
                                <li class="nav-item">
                                    <a class="nav-link" @click="addPricingOption"><strong><i
                                                class="fa-solid fa-plus"></i> Add Option</strong></a>
                                </li>
                            </ul>
                            <div class="tab-content" style="max-height: 850px; overflow-y: auto;">
                                <div v-if="activeProductList">
                                    <table class="product-table">
                                        <thead>
                                            <tr>
                                                <th>Product</th>
                                                <th>Description</th>
                                                <th>Notes</th>
                                                <th>Qty</th>
                                                <th>Price</th>
                                                <th>Discount</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr v-for="(item, index) in activeProductList" :key="index">
                                                <td class="product-name-cell" :title="item.product"
                                                    :style="item.isChild ? 'padding-left: 2.5rem;' : ''"
                                                    @click.stop="activeTabIndex === 0 ? startEdit(index, 'product') : startEditOption(activeProductList, index, 'product')">
                                                    <div style="display: flex; align-items: center; gap: 8px;">
                                                        <i v-if="item.isChild"
                                                            class="fas fa-level-up-alt fa-rotate-90 text-vertex-blue"></i>

                                                        <i v-if="item.isChild" class="fas"
                                                            :class="item.item_hidden ? 'fa-eye-slash text-danger' : 'fa-eye text-success'"
                                                            style="cursor: pointer;"
                                                            :title="item.item_hidden ? 'Hidden from Proposal PDF' : 'Visible on Proposal PDF'"
                                                            @click.stop="item.item_hidden = !item.item_hidden; hasPricingTableChanges = true"></i>

                                                         <i v-if="!item.isChild || item.pricing > 0 || item.defaultPricing > 0" class="fa-solid fa-times remove-icon"
                                                             title="Remove Product"
                                                             @click.stop="removeProduct(index, activeProductList)"></i>

                                                         <strong v-if="!item.isEditingProduct"
                                                             :style="item.item_hidden ? 'text-decoration: line-through; color: #999;' : ''">{{
                                                             item.product }}</strong>
                                                         <input v-else v-focus type="text" v-model="item.product"
                                                             class="product-name-input"
                                                             @click.stop
                                                             @blur="activeTabIndex === 0 ? endEdit(item, 'product') : endEditOption(item, 'product')"
                                                             @keyup.enter="activeTabIndex === 0 ? endEdit(item, 'product') : endEditOption(item, 'product')" />
                                                    </div>
                                                </td>

                                                <td :title="stripHtml(item.description || '')"
                                                    @click.stop="openRichTextModal(item, 'description')" style="cursor: pointer; text-align: left;">
                                                    <span>{{ truncateText(stripHtml(item.description), 50) }}</span>
                                                </td>

                                                <td :title="stripHtml(item.remarks || '')"
                                                    @click.stop="openRichTextModal(item, 'remarks')" style="cursor: pointer; text-align: left;">
                                                    <span>{{ truncateText(stripHtml(item.remarks), 50) }}</span>
                                                </td>

                                                <td style="text-align: center;"
                                                    @click.stop="(!item.isChild || item.pricing > 0 || item.defaultPricing > 0) ? (activeTabIndex === 0 ? startEdit(index, 'quantity') : startEditOption(activeProductList, index, 'quantity')) : null">
                                                    <span v-if="!item.isEditingQuantity || (item.isChild && parseFloat(item.pricing) <= 0 && parseFloat(item.defaultPricing || 0) <= 0)">{{
                                                        item.quantity }}</span>
                                                    <input v-else type="number" v-model.number="item.quantity"
                                                        @blur="activeTabIndex === 0 ? endEdit(item, 'quantity') : endEditOption(item, 'quantity')"
                                                        @keyup.enter="activeTabIndex === 0 ? endEdit(item, 'quantity') : endEditOption(item, 'quantity')"
                                                        @input="syncChildQuantities(item, activeProductList)" />
                                                </td>

                                                <td
                                                    @click.stop="(!item.isChild || item.pricing > 0 || item.defaultPricing > 0) ? (activeTabIndex === 0 ? startEdit(index, 'pricing') : startEditOption(activeProductList, index, 'pricing')) : null">
                                                    <template v-if="!item.isChild || item.pricing > 0 || item.defaultPricing > 0">
                                                        <span v-if="!item.isEditingPricing">{{
                                                            formatCurrency(item.pricing) }}</span>
                                                        <input v-else type="number" v-model.number="item.pricing"
                                                            @blur="activeTabIndex === 0 ? endEdit(item, 'pricing') : endEditOption(item, 'pricing')"
                                                            @keyup.enter="activeTabIndex === 0 ? endEdit(item, 'pricing') : endEditOption(item, 'pricing')" />
                                                    </template>
                                                    <template v-else>
                                                        <span class="badge-included">Included</span>
                                                    </template>
                                                </td>

                                                <td
                                                    @click.stop="(!item.isChild || item.pricing > 0 || item.defaultPricing > 0) ? (activeTabIndex === 0 ? startEdit(index, 'discount') : startEditOption(activeProductList, index, 'discount')) : null">
                                                    <template v-if="!item.isChild || item.pricing > 0 || item.defaultPricing > 0">
                                                        <span v-if="!item.isEditingDiscount">{{
                                                            formatCurrency(item.discount) }}</span>
                                                        <input v-else type="number" v-model.number="item.discount"
                                                            @blur="activeTabIndex === 0 ? endEdit(item, 'discount') : endEditOption(item, 'discount')"
                                                            @keyup.enter="activeTabIndex === 0 ? endEdit(item, 'discount') : endEditOption(item, 'discount')" />
                                                    </template>
                                                </td>
                                            </tr>
                                        </tbody>
                                        <tfoot v-if="activeProductList && activeProductList.length > 0" class="pricing-summary-footer">
                                            <tr>
                                                <td colspan="4" class="summary-label text-muted">Subtotal (Excl. GST):</td>
                                                <td colspan="2" class="summary-value">{{ formatCurrency(calculateSubTotalPrice) }}</td>
                                            </tr>
                                            <tr>
                                                <td colspan="4" class="summary-label text-muted">GST (10%):</td>
                                                <td colspan="2" class="summary-value">{{ formatCurrency(calculateTotalGST) }}</td>
                                            </tr>
                                            <tr class="total-row">
                                                <td colspan="4" class="summary-label total-label">Total (Incl. GST):</td>
                                                <td colspan="2" class="summary-value total-value">{{ formatCurrency(calculateTotalPrice) }}</td>
                                            </tr>
                                            <tr>
                                                <td colspan="6" style="padding: 12px; background-color: #f9f9f9;">
                                                    <div style="display: flex; justify-content: flex-end; gap: 10px;">
                                                        <button class="button-select continue" @click.stop="buildPricingTable()"
                                                            :disabled="!hasPricingTableChanges">
                                                            <i class="fa-solid fa-wrench"></i> &nbsp; Build Table
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        </tfoot>
                                    </table>
                                </div>
                            </div>
                        </div>
                        <div class="right-column">
                            <div style="padding: 10px;">
                                <input type="text" v-model="searchQuery" placeholder="Search products..."
                                    class="product-search-input">
                                <div class="available-count-label">
                                    <i class="fa-solid fa-list"></i>
                                    {{ filteredProducts.length }} of {{ availableProducts.length }} Available
                                </div>
                                <div style="max-height: 70vh; overflow-y: auto;">
                                    <table class="product-selection-table">
                                        <thead>
                                            <tr>
                                                <th>Product</th>
                                                <th>Price</th>
                                                <th style="width: 50px; text-align: center;"></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr v-for="(product, index) in filteredProducts" :key="index">
                                                <td style="color: #00bcd4"><strong>{{ product.name }}</strong></td>
                                                <td>{{ formatCurrency(product.defaultPricing) }}</td>
                                                <td style="width: 50px; text-align: center; padding: 5px;">
                                                    <button title="Add line item"
                                                        class="button-select continue add-btn-small"
                                                        @click.stop="selectGeneralProduct(product, activeProductList)">
                                                        <i class="fa-solid fa-plus"></i>
                                                    </button>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <!-- Rich Text Editing Modal -->
                <div v-if="showRichTextModal" class="rich-text-modal-backdrop">
                  <div class="rich-text-modal">
                    <div class="rich-text-modal-header">
                      <h5>Edit {{ richTextEditingField === 'description' ? 'Description' : 'Notes' }}</h5>
                      <button type="button" class="btn-close-custom" @click="closeRichTextModal">&times;</button>
                    </div>
                    <div class="rich-text-modal-body">
                      <div class="rich-text-toolbar">
                        <button type="button" class="toolbar-btn" @mousedown.prevent="execEditorCommand('bold')" title="Bold">
                          <i class="fa-solid fa-bold"></i>
                        </button>
                        <button type="button" class="toolbar-btn" @mousedown.prevent="execEditorCommand('italic')" title="Italic">
                          <i class="fa-solid fa-italic"></i>
                        </button>
                        <button type="button" class="toolbar-btn" @mousedown.prevent="execEditorCommand('underline')" title="Underline">
                          <i class="fa-solid fa-underline"></i>
                        </button>
                        <button type="button" class="toolbar-btn" @mousedown.prevent="execEditorCommand('insertUnorderedList')" title="Bullet List">
                          <i class="fa-solid fa-list-ul"></i>
                        </button>
                      </div>
                      <div 
                        ref="richTextEditor" 
                        class="rich-text-editor" 
                        contenteditable="true"
                        @input="onEditorInput"
                        style="text-align: left;"
                      ></div>
                      <div 
                        class="rich-text-char-counter" 
                        :style="{ color: richTextLength > 254 ? '#dc3545' : '#6c757d' }"
                        v-if="richTextEditingField === 'remarks'"
                      >
                        {{ richTextLength }}/254
                      </div>
                    </div>
                    <div class="rich-text-modal-footer">
                      <button type="button" class="btn-cancel" @click="closeRichTextModal">Cancel</button>
                      <button 
                        type="button" 
                        class="btn-save" 
                        @click="saveRichTextContent"
                        :disabled="richTextEditingField === 'remarks' && richTextLength > 254"
                      >Save</button>
                    </div>
                  </div>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
import SimpleLoader from '@/components/Utilities/SimpleLoader.vue';
import { Modal } from 'bootstrap';
import { isEmpty } from '@/plugins/helper.js';

export default {
    name: 'PricingTableModal',
    components: {
        SimpleLoader,
    },
    directives: {
        focus: {
            mounted(el) {
                el.focus();
            }
        }
    },
    props: {
        setItems: {
            type: Object,
        },
        isLoading: {
            type: Boolean,
            default: false
        }
    },
    emits: ['update-pricing-table', 'save-proposal', 'has-changes'],
    data() {
        return {
            pricingTableModal: null,
            showRichTextModal: false,
            richTextEditingItem: null,
            richTextEditingField: '',
            richTextLength: 0,
            isSelectingProduct: false,
            pricingOptionCount: 1,

            rawAuthProducts: [],
            rawInternalPricing: [],
            allMappedProducts: [],
            availableProducts: [],
            tempAvailableProducts: [],

            productListOptions: [],
            productList: [],
            newProductIndex: -1,
            isCharityLead: false,
            hasPricingTableChanges: false,
            searchQuery: '',
            activeTabIndex: 0,
        }
    },
    computed: {
        activeProductList() {
            if (this.activeTabIndex === 0) {
                return this.productList;
            }
            const option = this.productListOptions[this.activeTabIndex - 1];
            return option ? option.productList : [];
        },
        activeParentProductCount() {
            return (this.activeProductList || []).filter(p => !p.isChild).length;
        },
        calculateSubTotalPrice() {
            return (this.activeProductList || []).reduce((total, product) => {
                if (product.item_hidden || (product.isChild && parseFloat(product.pricing) <= 0)) return total;
                const productTotal = (parseFloat(product.pricing) || 0) * (parseInt(product.quantity) || 0) - (product.discount * parseInt(product.quantity) || 0);
                return (total + productTotal);
            }, 0).toFixed(2);
        },

        calculateTotalPrice() {
            return (this.activeProductList || []).reduce((total, product) => {
                if (product.item_hidden || (product.isChild && parseFloat(product.pricing) <= 0)) return total;
                const productTotal = ((parseFloat(product.pricing) || 0) * (parseInt(product.quantity) || 0) - (product.discount * parseInt(product.quantity) || 0)) * 1.1;
                return (total + productTotal);
            }, 0).toFixed(2);
        },

        calculateTotalGST() {
            return (this.activeProductList || []).reduce((total, product) => {
                if (product.item_hidden || (product.isChild && parseFloat(product.pricing) <= 0)) return total;
                const productTotal = ((parseFloat(product.pricing) || 0) * (parseInt(product.quantity) || 0) - (product.discount * parseInt(product.quantity) || 0)) * 0.1;
                return (total + productTotal);
            }, 0).toFixed(2);
        },
        filteredProducts() {
            if (!this.searchQuery) {
                return this.availableProducts;
            }
            return this.availableProducts.filter(product =>
                product.name.toLowerCase().includes(this.searchQuery.toLowerCase())
            );
        },
    },
    watch: {
        hasPricingTableChanges(newVal) {
            this.$emit('has-changes', newVal);
        },
        setItems: {
            immediate: true,
            deep: true,
            async handler(Val) {
                if (!isEmpty(Val) && !isEmpty(Val.items)) {
                    if (this.rawAuthProducts.length === 0 || this.rawInternalPricing.length === 0) {
                        await this.loadProductData();
                    }
                    this.tempAvailableProducts = JSON.parse(JSON.stringify(this.availableProducts))
                    this.productList = []
                    this.productListOptions = []
                    this.pricingOptionCount = 1
                    this.activeTabIndex = 0
                    this.convertLeadProductsToSelectedProducts(Val.items)
                    this.availableProducts = JSON.parse(JSON.stringify(this.tempAvailableProducts))
                }
            }
        },
    },
    mounted() {
        this.pricingTableModal = new Modal(this.$refs.pricingTableModal);
        this.loadProductData();
    },
    methods: {
        truncateText(text, length = 50) {
            if (!text) return "";
            if (text.length <= length) return text;
            return text.substring(0, length) + "...";
        },
        stripHtml(html) {
            if (!html) return "";
            return html.replace(/<[^>]*>/g, "");
        },
        openRichTextModal(item, field) {
            this.richTextEditingItem = item;
            this.richTextEditingField = field;
            this.showRichTextModal = true;
            let val = item[field] || "";
            val = this.convertToHtml(val);
            this.richTextLength = val.length;
            this.$nextTick(() => {
                const editor = this.$refs.richTextEditor;
                if (editor) {
                    editor.innerHTML = val;
                    editor.focus();
                }
            });
        },
        convertToHtml(text) {
            if (!text) return "";
            if (/<[a-z][\s\S]*>/i.test(text)) {
                return text;
            }
            return text.replace(/\r?\n/g, "<br>");
        },
        execEditorCommand(command) {
            document.execCommand(command, false, null);
            this.updateLength();
        },
        updateLength() {
            const editor = this.$refs.richTextEditor;
            if (editor) {
                this.richTextLength = editor.innerHTML.length;
            }
        },
        onEditorInput(e) {
            this.richTextLength = e.target.innerHTML.length;
        },
        closeRichTextModal() {
            this.showRichTextModal = false;
            this.richTextEditingItem = null;
            this.richTextEditingField = '';
            this.richTextLength = 0;
        },
        saveRichTextContent() {
            const editor = this.$refs.richTextEditor;
            if (editor && this.richTextEditingItem) {
                let content = editor.innerHTML;
                if (content === '<br>' || content === '<p><br></p>' || content === '<div><br></div>') {
                    content = '';
                }
                this.richTextEditingItem[this.richTextEditingField] = content;
                this.hasPricingTableChanges = true;
            }
            this.closeRichTextModal();
        },

        openModal() {
            this.activeTabIndex = 0;
            this.pricingTableModal.show();
        },
        closeModal() {
            this.pricingTableModal.hide();
        },
        calculateOptionSubtotalPrice(productList) {
            return productList.reduce((total, product) => {
                if (product.item_hidden || (product.isChild && parseFloat(product.pricing) <= 0)) return total;
                const productTotal = ((parseFloat(product.pricing) || 0) * (parseInt(product.quantity) || 0) - (product.discount * parseInt(product.quantity) || 0));
                return total + productTotal;
            }, 0).toFixed(2);
        },

        calculateOptionTotalPrice(productList) {
            return productList.reduce((total, product) => {
                if (product.item_hidden || (product.isChild && parseFloat(product.pricing) <= 0)) return total;
                const productTotal = ((parseFloat(product.pricing) || 0) * (parseInt(product.quantity) || 0) - (product.discount * parseInt(product.quantity) || 0)) * 1.1;
                return total + productTotal;
            }, 0).toFixed(2);
        },

        calculateOptionGST(productList) {
            return productList.reduce((total, product) => {
                if (product.item_hidden || (product.isChild && parseFloat(product.pricing) <= 0)) return total;
                const productTotal = ((parseFloat(product.pricing) || 0) * (parseInt(product.quantity) || 0) - (product.discount * parseInt(product.quantity) || 0)) * 0.1;
                return total + productTotal;
            }, 0).toFixed(2);
        },

        toOptionName(count) {
            if (count <= 1) return '';
            let result = '';
            let num = count;
            while (num > 0) {
                let remainder = num % 26;
                if (remainder === 0) {
                    remainder = 26;
                }
                result = String.fromCharCode(64 + remainder) + result;
                num = Math.floor((num - remainder) / 26);
            }
            return result;
        },

        isUUID(uuid) {
            if (typeof uuid !== 'string') return false;
            if (uuid.length !== 36) return false;
            if (uuid[8] !== '-' || uuid[13] !== '-' || uuid[18] !== '-' || uuid[23] !== '-') return false;
            return true;
        },

        async loadProductData() {
            await Promise.all([
                this.getAuthProducts(),
                this.getInternalPricing(),
            ]);
            this.buildAvailableProductsList();
        },

        async getAuthProducts() {
            await this.$axios.get("/auth-products")
                .then((response) => {
                    this.rawAuthProducts = response.data.authProducts || [];
                })
                .catch((error) => console.error(error));
        },

        async getInternalPricing() {
            await this.$axios.get("/get-product-pricing")
                .then((response) => {
                    this.rawInternalPricing = response.data.productPrices || [];
                })
                .catch((error) => console.error(error));
        },

        buildAvailableProductsList() {
            this.availableProducts = [];
            this.allMappedProducts = [];

            this.rawAuthProducts.forEach(authItem => {
                if (!authItem.Product) return;

                let price = authItem.Product.price ?? authItem.Product.Price ?? authItem.price ?? authItem.Price ?? 0;
                let description = authItem.Product.description ?? authItem.Product.Description ?? authItem.description ?? "No Product Description";
                let name = authItem.Product.display_name ?? authItem.Product.name ?? "No Name";
                let isCharity = authItem.is_charity_product === true || authItem.is_charity_product == 1;

                let parentIds = authItem.parent_product_ids || authItem.ParentProductIDs || authItem.parent_product_id || authItem.ParentProductID || null;
                if (typeof parentIds === 'string') {
                   try { parentIds = JSON.parse(parentIds); } catch(e) { parentIds = parentIds ? [parentIds] : []; }
                }
                if (!Array.isArray(parentIds)) parentIds = parentIds ? [parentIds] : [];
                parentIds = parentIds.filter(id => id && id !== '');

                this.allMappedProducts.push({
                    id: authItem.product_id || authItem.Product.id,
                    name: name,
                    defaultPricing: price,
                    description: description,
                    defaultQuantity: 1,
                    isCharityProduct: isCharity,
                    parentIds: parentIds,
                    pricingPlanType: authItem.pricing_plan_type || (authItem.Product && authItem.Product.pricing_plan_type) || ""
                });
            });

            // NEW: Merge parent-child relationships from rawInternalPricing into allMappedProducts
            this.allMappedProducts.forEach(p => {
                const internalMatches = this.rawInternalPricing.filter(ip => 
                    String(ip.product_id || ip.ProductID || ip.id) === String(p.id) || 
                    String(ip.auth_item_code || ip.AuthItemCode) === String(p.id)
                );
                
                internalMatches.forEach(ip => {
                    let ipParentIds = ip.ParentProductIDs || ip.parent_product_ids || ip.ParentProductID || ip.parent_product_id || null;
                    if (ipParentIds) {
                        if (typeof ipParentIds === 'string') {
                            try { ipParentIds = JSON.parse(ipParentIds); } catch(e) { ipParentIds = [ipParentIds]; }
                        }
                        if (!Array.isArray(ipParentIds)) ipParentIds = [ipParentIds];
                        
                        ipParentIds.forEach(pid => {
                            if (pid && !p.parentIds.includes(pid)) p.parentIds.push(pid);
                        });
                    }
                });
            });

            // Show ONLY parent products (roots) in the right selection table
            this.availableProducts = this.allMappedProducts.filter(p => {
                if (!p.parentIds || p.parentIds.length === 0) return true;
                const parentExists = p.parentIds.some(pid => this.allMappedProducts.some(parent => parent.id === pid));
                return !parentExists;
            });

            this.availableProducts.sort((a, b) => a.name.localeCompare(b.name));
        },

        formatCurrency(value) {
            const numericValue = parseFloat(value);
            if (isNaN(numericValue)) {
                return '$0.00';
            }
            return `$${numericValue.toFixed(2)}`;
        },

        calculateTotal(item) {
            return (item.quantity * item.pricing) - (item.discount * item.quantity);
        },

        calculateTotalDiscount(item) {
            return (item.discount * item.quantity);
        },

        startEdit(index, field) {
            this.activeProductList.forEach((item, i) => {
                if (i === index) {
                    item[`isEditing${field.charAt(0).toUpperCase() + field.slice(1)}`] = true;
                } else {
                    item.isEditingProduct = false;
                    item.isEditingDescription = false;
                    item.isEditingRemarks = false;
                    item.isEditingQuantity = false;
                    item.isEditingPricing = false;
                    item.isEditingDiscount = false;
                }
            });
        },

        endEdit(item, field) {
            item[`isEditing${field.charAt(0).toUpperCase() + field.slice(1)}`] = false;
            this.hasPricingTableChanges = true;
        },

        startEditOption(productList, index, field) {
            productList.forEach((item, i) => {
                if (i === index) {
                    item[`isEditing${field.charAt(0).toUpperCase() + field.slice(1)}`] = true;
                } else {
                    item.isEditingProduct = false;
                    item.isEditingDescription = false;
                    item.isEditingRemarks = false;
                    item.isEditingQuantity = false;
                    item.isEditingPricing = false;
                    item.isEditingDiscount = false;
                }
            });
        },

        endEditOption(item, field) {
            item[`isEditing${field.charAt(0).toUpperCase() + field.slice(1)}`] = false;
            this.hasPricingTableChanges = true;
        },

        syncChildQuantities(parentItem, list) {
            if (!parentItem.isChild) {
                list.forEach(item => {
                    if (item.isChild && item.uiParentId === parentItem.uiParentId) {
                        item.quantity = parentItem.quantity;
                        this.hasPricingTableChanges = true;
                    }
                });
            }
        },

        selectGeneralProduct(selectedProduct, targetList) {
            const uiParentId = Date.now() + Math.random();
            const isCharityFree = selectedProduct.isCharityProduct && this.isCharityLead;
            const pricingToUse = isCharityFree ? 0 : (selectedProduct.defaultPricing || 0.00);
            const discountToUse = isCharityFree ? 0 : (selectedProduct.defaultDiscount || 0.00);

            targetList.push({
                id: selectedProduct.id,
                product: selectedProduct.name,
                description: this.convertToHtml(selectedProduct.description || "No Product Description"),
                remarks: selectedProduct.remarks || "",
                quantity: selectedProduct.defaultQuantity || 1,
                discount: discountToUse,
                pricing: pricingToUse,
                isChild: false,
                uiParentId: uiParentId,
                item_hidden: false,
                planType: selectedProduct.pricingPlanType || ""
            });

            // Automatically add its children
            const children = this.allMappedProducts.filter(p => p.parentIds && p.parentIds.includes(selectedProduct.id));
            children.forEach(childProd => {
                const childPrice = childProd.defaultPricing || 0.00;
                targetList.push({
                    id: childProd.id,
                    product: childProd.name,
                    description: this.convertToHtml(childProd.description || "No Product Description"),
                    remarks: childProd.remarks || "",
                    quantity: selectedProduct.defaultQuantity || 1,
                    discount: 0,
                    pricing: childPrice,
                    isChild: true,
                    parentId: selectedProduct.id,
                    uiParentId: uiParentId,
                    item_hidden: false,
                    planType: childProd.pricingPlanType || "",
                    defaultPricing: childPrice
                });
            });

            this.hasPricingTableChanges = true;
        },

        removeProduct(indexToRemove, targetList) {
            if (targetList.length <= 1) {
                alert("Cannot remove last product on table. Please add another one for replacement.");
                return;
            }

            const item = targetList[indexToRemove];
            if (!item) return;

            if (!item.isChild) {
                // Remove parent AND all its associated children
                const parentUiId = item.uiParentId;
                const filtered = targetList.filter(p => p.uiParentId !== parentUiId);
                targetList.splice(0, targetList.length, ...filtered);
            } else {
                // Remove only this child product
                targetList.splice(indexToRemove, 1);
            }

            this.hasPricingTableChanges = true;
        },

        convertLeadProductsToSelectedProducts(products_array) {
            if (!isEmpty(products_array) && products_array.length > 0) {
                products_array.forEach((p) => {
                    // RESOLVE ID: Match ClientProposal's logic for UUID -> AuthCode mapping
                    let targetAuthId = String(p.product_id || p.id);
                    if (this.rawInternalPricing && this.rawInternalPricing.length > 0) {
                        const internalMap = this.rawInternalPricing.find(ip => 
                            String(ip.product_id || ip.ProductID || ip.id) === targetAuthId || 
                            String(ip.auth_item_code || ip.AuthItemCode) === targetAuthId
                        );
                        if (internalMap) {
                            const isAuthLinked = internalMap.has_auth_product_linked == 1 || internalMap.HasAuthProductLinked == 1 || internalMap.has_auth_product_linked === true || internalMap.HasAuthProductLinked === true;
                            if (isAuthLinked && (internalMap.AuthItemCode || internalMap.auth_item_code)) {
                                targetAuthId = String(internalMap.AuthItemCode || internalMap.auth_item_code);
                            }
                        }
                    }

                    let productFromList = this.allMappedProducts.find(item => String(item.id) === targetAuthId);
                    if (!productFromList) return;

                    this.isCharityLead = p.is_charity;

                    const customProductName = p.product_name || p.product || (p.Product && (p.Product.display_name || p.Product.name));

                    const newProduct = {
                        id: productFromList.id,
                        product: customProductName || productFromList.name,
                        description: this.convertToHtml(p.description || productFromList.description || "No Product Description"),
                        remarks: p.remarks || "",
                        quantity: p.quantity || 1,
                        discount: p.discount !== undefined ? p.discount : 0.00,
                        pricing: p.price !== undefined ? p.price : (productFromList.defaultPricing || 0.00),
                        isChild: !!(productFromList.parentIds && productFromList.parentIds.length > 0) || 
                                 p.is_child === true || p.is_child === 1 || String(p.is_child) === "true" ||
                                 p.isChild === true || p.isChild === 1 || String(p.isChild) === "true",
                        parentIds: productFromList.parentIds,
                        uiParentId: Date.now() + Math.random(), // Temp assign, will be aligned below
                        item_hidden: p.item_hidden || false,
                        planType: productFromList.pricingPlanType || "",
                        defaultPricing: productFromList.defaultPricing || 0.00
                    };

                    if (p.option_index == 1) {
                        this.productList.push(newProduct);
                    } else if (p.option_index >= 2) {
                        while (this.productListOptions.length < p.option_index - 1) {
                            this.productListOptions.push({ productList: [] });
                        }
                        const option = this.productListOptions[p.option_index - 2];
                        option.productList.push(newProduct);
                    }
                })

                // Re-align children to their parents loaded from DB so they sync together
                // Uses a proximity-based search to correctly handle duplicate parents (e.g., two Yubikeys)
                const alignUiParents = (list) => {
                    let lastSeenParents = {}; // productId -> uiParentId
                    list.forEach(item => {
                        if (!item.isChild) {
                            // This is a parent, keep track of its UI ID
                            lastSeenParents[item.id] = item.uiParentId;
                        } else if (item.parentIds && item.parentIds.length > 0) {
                            // This is a child, find the most recent parent of its type
                            const matchingParentId = item.parentIds.find(pid => lastSeenParents[pid]);
                            if (matchingParentId) {
                                item.uiParentId = lastSeenParents[matchingParentId];
                            }
                        }
                    });
                };
                alignUiParents(this.productList);
                this.productListOptions.forEach(opt => alignUiParents(opt.productList));

                this.pricingOptionCount = this.productListOptions.length + 1
                this.hasPricingTableChanges = false;
            }
        },

        addPricingOption() {
            let pO = [];
            const sourceList = this.activeProductList;
            if (!isEmpty(sourceList)) {
                sourceList.forEach(p => {
                    pO.push(JSON.parse(JSON.stringify(p)))
                })
            }
            this.productListOptions.push({
                productList: pO,
            })
            this.pricingOptionCount += 1
            this.hasPricingTableChanges = true;
            this.activeTabIndex = this.productListOptions.length;
        },

        removeOption(productOption) {
            const index = this.productListOptions.indexOf(productOption)
            if (index > -1) {
                this.productListOptions.splice(index, 1)
                this.pricingOptionCount -= 1;
                this.hasPricingTableChanges = true;
                if (this.activeTabIndex > this.productListOptions.length) {
                    this.activeTabIndex = this.productListOptions.length;
                }
            }
        },

        buildPricingTable() {
    //         let productsToPropagate = ""
    //         let optionsToPropagate = ""

    //         this.productList.forEach(p => {
    //             if (p.item_hidden) return;

    //             let productCell = p.isChild
    //                 ? `<span style="padding-left: 20px; color: #666;">&#8627; ${p.product}</span>`
    //                 : `<b>${p.product}</b>`;

    //             let priceCell = p.isChild ? "" : this.formatCurrency(p.pricing);
    //             let discountCell = p.isChild ? "" : this.formatCurrency(p.discount);
    //             let totalDiscountCell = p.isChild ? "" : this.formatCurrency(this.calculateTotalDiscount(p));
    //             let totalCell = p.isChild ? "" : this.formatCurrency(this.calculateTotal(p));

    //             productsToPropagate +=
    //                 `<tr id='product_list_${p.id}'>
    //                    <td style="border-color: black; border-width: 1px; text-align: left; padding-left: 8px;">${productCell}</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">${p.description || "No Product Description"}</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">${p.remarks || "No Remarks"}</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">${p.quantity}</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">${priceCell}</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">${discountCell}</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">${totalDiscountCell}</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">${totalCell}</td>
    //                 </tr>
    //             `})

    //         productsToPropagate += `
    //                 <tr>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;"><b>Subtotal<b></td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">${this.formatCurrency(this.calculateSubTotalPrice)}</td>
    //   </tr>
    //   <tr>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //       <td title="Goods and Services Tax" style="border-color: black; border-width: 1px; text-align: center;"><b>GST (10%)<b></td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">${this.formatCurrency(this.calculateTotalGST)}</td>
    //   </tr>
    //   <tr>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //        <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;"><b>Total AUD<b></td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">${this.formatCurrency(this.calculateTotalPrice)}</td>
    //   </tr>
    //             `
    //         if (!isEmpty(this.productListOptions))
    //             this.productListOptions.forEach((plo, index) => {
    //                 let optionBody = ""

    //                 if (!isEmpty(plo.productList)) {
    //                     plo.productList.forEach(p => {
    //                         if (p.item_hidden) return;

    //                         let productCell = p.isChild
    //                             ? `<div style="padding-left: 15px; display: flex; align-items: center; color: #555;">
    //               <span style="color: #00a0c1; font-weight: bold; margin-right: 8px; font-size: 1.2em;">&#9658;</span> 
    //               <span>${p.product}</span>
    //             </div>`
    //                             : `<b>${p.product}</b>`;

    //                         let priceCell = p.isChild ? "" : this.formatCurrency(p.pricing);
    //                         let discountCell = p.isChild ? "" : this.formatCurrency(p.discount);
    //                         let totalDiscountCell = p.isChild ? "" : this.formatCurrency(this.calculateTotalDiscount(p));
    //                         let totalCell = p.isChild ? "" : this.formatCurrency(this.calculateTotal(p));

    //                         optionBody +=
    //                             `<tr id='product_list_${p.id}'>
    //                     <td style="border-color: black; border-width: 1px; text-align: left; padding-left: 8px;">${productCell}</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">${p.description || "No Product Description"}</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">${p.remarks || "No Remarks"}</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">${p.quantity}</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">${priceCell}</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">${discountCell}</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">${totalDiscountCell}</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">${totalCell}</td>
    //                 </tr>
    //             `})

    //                     optionBody += `
    //                  <tr>
    //                     <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //                     <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //                     <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //                     <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //                     <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //                     <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //                     <td style="border-color: black; border-width: 1px; text-align: center;"><b>Subtotal<b></td>
    //                     <td style="border-color: black; border-width: 1px; text-align: center;">${this.formatCurrency(this.calculateOptionSubtotalPrice(plo.productList))}</td>
    //                 </tr>
    //                 <tr>
    //                     <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //                     <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //                     <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //                     <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //                     <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //                     <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //                     <td title="Goods and Services Tax" style="border-color: black; border-width: 1px; text-align: center;"><b>GST (10%)<b></td>
    //                     <td style="border-color: black; border-width: 1px; text-align: center;">${this.formatCurrency(this.calculateOptionGST(plo.productList))}</td>
    //                 </tr>
    //                 <tr>
    //                     <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //                     <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //                     <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //                     <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //                     <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //                     <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //                     <td style="border-color: black; border-width: 1px; text-align: center;"><b>Total AUD<b></td>
    //                     <td style="border-color: black; border-width: 1px; text-align: center;">${this.formatCurrency(this.calculateOptionTotalPrice(plo.productList))}</td>
    //                 </tr>
    //             `

    //                     optionsToPropagate += `
                
    //             <br>
    //             <span class="orange-label-product-option" style="margin-left: 0;">Pricing for Option ${this.toOptionName(index + 2)}</span>
    //             <table style="border-collapse: collapse; width: 100%; border-color: black; border: 2px solid black;" border="1">
    //             <thead>
    //                 <tr>
    //                     <th style="background-color: #00a0c1; border-color: black; color: white; border-width: 1px; text-align: left; padding-left: 8px;">Product</th>
    //                     <th style="background-color: #00a0c1; border-color: black; color: white; border-width: 1px; text-align: center;">Description</th>
    //                     <th style="background-color: #00a0c1; border-color: black; color: white; border-width: 1px; text-align: center;">Notes</th>
    //                     <th style="background-color: #00a0c1; border-color: black; color: white; border-width: 1px; text-align: center;">Qty</th>
    //                     <th style="background-color: #00a0c1; border-color: black; color: white; border-width: 1px; text-align: center;">Price</th>
    //                     <th style="background-color: #00a0c1; border-color: black; color: white; border-width: 1px; text-align: center;">Per Item Discount</th>
    //                     <th style="background-color: #00a0c1; border-color: black; color: white; border-width: 1px; text-align: center;">Total Discount</th>
    //                     <th style="background-color: #00a0c1; border-color: black; color: white; border-width: 1px; text-align: center;">Total AUD</th>
    //                 </tr>
    //             </thead>
    //             <tbody>
    //                 ${optionBody}
    //             </tbody>
    //             </table>
    //             `
    //                 }
    //             })

    //         this.$emit('update-pricing-table', `<table style="border-collapse: collapse; width: 100%; border-color: black; border: 2px solid black;" border="1">
    //             <thead>
    //                 <tr>
    //                      <th style="background-color: #00a0c1; border-color: black; color: white; border-width: 1px; text-align: left; padding-left: 8px;">Product</th>
    //                      <th style="background-color: #00a0c1; border-color: black; color: white; border-width: 1px; text-align: center;">Description</th>
    //                      <th style="background-color: #00a0c1; border-color: black; color: white; border-width: 1px; text-align: center;">Notes</th>
    //                      <th style="background-color: #00a0c1; border-color: black; color: white; border-width: 1px; text-align: center;">Qty</th>
    //                      <th style="background-color: #00a0c1; border-color: black; color: white; border-width: 1px; text-align: center;">Price</th>
    //                      <th style="background-color: #00a0c1; border-color: black; color: white; border-width: 1px; text-align: center;">Per Item Discount</th>
    //                      <th style="background-color: #00a0c1; border-color: black; color: white; border-width: 1px; text-align: center;">Total Discount</th>
    //                      <th style="background-color: #00a0c1; border-color: black; color: white; border-width: 1px; text-align: center;">Total AUD</th>
    //                 </tr>
    //             </thead>
    //             <tbody>
    //                 ${productsToPropagate}
    //             </tbody>
    //             </table>
    //                 ${(!isEmpty(optionsToPropagate) ? optionsToPropagate : "")}
    //         `);

            this.hasPricingTableChanges = false;

            let proposal_items = []
            this.productList.forEach((p) => {
                proposal_items.push({
                    product_id: p.id.toString(),
                    product_name: p.product,
                    description: p.description,
                    price: (p.isChild && p.pricing <= 0) ? 0 : p.pricing,
                    quantity: p.quantity,
                    discount: (p.isChild && p.pricing <= 0) ? 0 : p.discount,
                    is_child: p.isChild || false,
                    option_index: 1,
                    remarks: p.remarks,
                    item_hidden: p.item_hidden || false
                })
            })

            if (!isEmpty(this.productListOptions))
                this.productListOptions.forEach((plo, index) => {
                    if (!isEmpty(plo.productList)) {
                        plo.productList.forEach((p) => {
                            proposal_items.push({
                                product_id: p.id.toString(),
                                product_name: p.product,
                                description: p.description,
                                price: (p.isChild && p.pricing <= 0) ? 0 : p.pricing,
                                quantity: p.quantity,
                                discount: (p.isChild && p.pricing <= 0) ? 0 : p.discount,
                                is_child: p.isChild || false,
                                option_index: index + 2,
                                remarks: p.remarks,
                                item_hidden: p.item_hidden || false
                            })
                        })
                    }
                })

            this.$emit('save-proposal', proposal_items);
            this.closeModal();
        },
    }
}
</script>
<style scoped>
.pricing-summary-footer {
    background: linear-gradient(180deg, rgba(0, 188, 212, 0.02) 0%, rgba(0, 188, 212, 0.08) 100%);
    border-top: 2px solid #00bcd4;
}
.pricing-summary-footer td {
    padding: 10px 16px;
    vertical-align: middle;
    border-bottom: 1px solid rgba(0, 188, 212, 0.1) !important;
}
.pricing-summary-footer .summary-label {
    text-align: right;
    font-size: 0.95rem;
    font-weight: 600;
    letter-spacing: 0.3px;
    color: #6c757d;
}
.pricing-summary-footer .summary-value {
    text-align: right;
    font-weight: 700;
    font-size: 1.05rem;
    color: #333;
}
.pricing-summary-footer .total-row td {
    border-bottom: none !important;
    padding-top: 14px;
    padding-bottom: 14px;
}
.pricing-summary-footer .total-label {
    color: #212529;
    font-size: 1.1rem;
    text-transform: uppercase;
}
.pricing-summary-footer .total-value {
    color: #00bcd4;
    font-size: 1.25rem;
}

.two-column-layout {
    display: flex;
    gap: 20px;
}

.left-column {
    flex: 2;
    overflow-x: auto;
}

.right-column {
    flex: 1;
    border: 1px solid #ddd;
    border-radius: 8px;
    display: flex;
    flex-direction: column;
}

.pricing-modal-header {
    background-color: #00A0C1;
    /* Vertex Blue */
    color: white;
}

.pricing-modal-header .modal-title {
    color: white;
}

.pricing-modal-header .btn-close {
    filter: invert(1) grayscale(100%) brightness(200%);
}

.product-table {
    width: 100%;
    border-collapse: collapse;
    margin-bottom: 20px;
    border: 1px solid #ddd;
}

.product-table th,
.product-table td {
    padding: 12px;
    text-align: left;
    border-bottom: 1px solid #ddd;
}

.product-table th {
    background-color: #00bcd4;
    color: white;
    font-weight: bold;
}

.product-table tr:last-child td {
    border-bottom: none;
}

.product-table td:first-child {
    align-items: center;
}

.product-table td:first-child>span {
    margin-right: 5px;
}

.add-product-row td {
    color: #00bcd4;
    cursor: pointer;
}

.text-vertex-blue {
    color: #00A0C1 !important;
}

.remove-icon {
    color: #dc3545;
    cursor: pointer;
    font-size: 1.1em;
    transition: transform 0.2s, color 0.2s;
}

.remove-icon:hover {
    color: #a71d2a;
    transform: scale(1.2);
}

.add-product-row td:first-child {
    font-weight: bold;
}

.add-product-row td:last-child {
    font-weight: bold;
    color: #ff8c00
}

.product-selection-list {
    background-color: #f9f9f9;
    border: 1px solid #ddd;
    padding: 10px;
    margin-top: 5px;
    border-radius: 5px;
}

.product-item {
    padding: 8px;
    cursor: pointer;
    transition: background-color 0.3s ease;
}

.product-item:hover {
    background-color: #eee;
}

.product-name-input {
    width: 100%;
    min-width: 140px;
    padding: 4px 8px;
    border: 1px solid #00bcd4;
    border-radius: 4px;
    font-size: 0.9em;
    font-weight: 600;
}

.product-name-input:focus {
    outline: none;
    border-color: #008ba3;
    box-shadow: 0 0 5px rgba(0, 188, 212, 0.5);
}

.product-table td input[type="number"] {
    width: 70px;
    padding: 6px;
    border: 1px solid #ccc;
    border-radius: 4px;
    font-size: 0.9em;
    text-align: left;
}

.product-table td input[type="number"]:focus {
    outline: none;
    border-color: #00bcd4;
    box-shadow: 0 0 5px rgba(0, 188, 212, 0.5);
}

.product-selection-table {
    width: 100%;
    border-collapse: collapse;
    margin-bottom: 16px;
}

.product-selection-table th,
.product-selection-table td {
    padding: 8px;
    border: 1px solid #ddd;
    text-align: left;
}

.product-selection-table th {
    background-color: #f2f2f2;
    font-weight: bold;
    position: sticky;
    top: 0;
    z-index: 1;
    background-color: #f2f2f2;
}

.product-item {
    padding: 0;
    margin: 0;
    border: none;
    cursor: pointer;
}

.total-row {
    font-weight: bold;
    background-color: #f9f9f9;
}

.total-label {
    text-align: right;
    padding-right: 8px;
}

.total-amount {
    text-align: right;
}

.continue {
    background-color: #00bcd4;
    color: white;
}

.continue:disabled {
    background-color: lightgray;
    color: white;
}

.actions {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
}

.button-select {
    padding: 10px 20px;
    border: none;
    border-radius: 5px;
    cursor: pointer;
    font-weight: bold;
}

.add-btn-small {
    padding: 6px 12px;
    font-size: 0.9em;
}

.orange-label-product-option {
    display: inline-block;
    background-color: #ff8c00;
    color: white;
    padding: 6px 12px;
    border-radius: 5px;
    font-weight: bold;
    font-size: 0.95em;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.char-counter {
    display: block;
    text-align: right;
    font-size: 0.75rem;
    color: #6c757d;
    margin-top: 2px;
}

.product-search-input {
    width: 100%;
    padding: 10px;
    margin-bottom: 10px;
    border: 1px solid #ddd;
    border-radius: 4px;
    box-sizing: border-box;
}

.nav-tabs .nav-link {
    cursor: pointer;
    color: black;
}

.nav-tabs .nav-link.active {
    background-color: #00A0C1;
    color: #fff !important;
    border-color: #00A0C1;
}

.remove-tab-icon {
    margin-left: 10px;
    cursor: pointer;
    font-weight: bold;
}

.modal-custom-width {
    max-width: 95vw !important;
}

.blur-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    backdrop-filter: blur(5px);
    background-color: rgba(0, 0, 0, 0.4);
}

.truncate-description {
    display: inline-block;
    max-width: 200px;
    /* Adjust based on your layout */
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    vertical-align: middle;
}

.product-count-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: rgba(255, 255, 255, 0.2);
    color: white;
    padding: 3px 12px;
    border-radius: 20px;
    font-size: 0.8rem;
    font-weight: 600;
    letter-spacing: 0.3px;
    border: 1px solid rgba(255, 255, 255, 0.3);
    vertical-align: middle;
}

.product-count-badge i {
    font-size: 0.75rem;
}

.available-count-label {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 0.8rem;
    font-weight: 600;
    color: #6c757d;
    margin-bottom: 8px;
    padding: 0 2px;
}

.available-count-label i {
    font-size: 0.7rem;
    color: #00bcd4;
}

/* Rich Text Modal Editor Styles */
.rich-text-modal-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    backdrop-filter: blur(5px);
    background-color: rgba(0, 0, 0, 0.4);
    z-index: 1060;
    display: flex;
    align-items: center;
    justify-content: center;
}
.rich-text-modal {
    background: #fff;
    border-radius: 12px;
    width: 600px;
    max-width: 90vw;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.25);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    animation: fadeIn 0.2s ease-out;
}
.rich-text-modal-header {
    background-color: #00A0C1;
    color: white;
    padding: 16px 20px;
    display: flex;
    align-items: center;
    justify-content: space-between;
}
.rich-text-modal-header h5 {
    margin: 0;
    font-weight: 600;
    font-size: 1.15rem;
}
.btn-close-custom {
    background: none;
    border: none;
    color: white;
    font-size: 1.5rem;
    cursor: pointer;
    line-height: 1;
    opacity: 0.8;
    transition: opacity 0.2s;
}
.btn-close-custom:hover {
    opacity: 1;
}
.rich-text-modal-body {
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 12px;
}
.rich-text-toolbar {
    display: flex;
    gap: 6px;
    background: #f1f3f5;
    padding: 8px;
    border-radius: 8px;
    border: 1px solid #dee2e6;
}
.toolbar-btn {
    background: white;
    border: 1px solid #ced4da;
    border-radius: 4px;
    padding: 6px 10px;
    font-size: 0.9rem;
    cursor: pointer;
    color: #495057;
    transition: all 0.2s;
}
.toolbar-btn:hover {
    background: #e9ecef;
    border-color: #adb5bd;
    color: #212529;
}
.rich-text-editor {
    border: 1px solid #ced4da;
    border-radius: 8px;
    padding: 12px;
    min-height: 150px;
    max-height: 300px;
    overflow-y: auto;
    font-size: 0.95rem;
    line-height: 1.6;
    color: #333;
    outline: none;
    background: white;
    text-align: left;
}
.rich-text-editor:focus {
    border-color: #00A0C1;
    box-shadow: 0 0 0 3px rgba(0, 160, 193, 0.15);
}
.rich-text-editor ul {
    margin-top: 0;
    margin-bottom: 8px;
    padding-left: 20px;
    list-style-type: disc !important;
}
.rich-text-editor li {
    margin-bottom: 4px;
}
.rich-text-char-counter {
    text-align: right;
    font-size: 0.8rem;
    color: #6c757d;
}
.rich-text-modal-footer {
    padding: 16px 20px;
    background: #f8f9fa;
    border-top: 1px solid #dee2e6;
    display: flex;
    justify-content: flex-end;
    gap: 12px;
}
.btn-cancel {
    background: white;
    border: 1px solid #ced4da;
    border-radius: 6px;
    padding: 8px 16px;
    font-weight: 600;
    cursor: pointer;
    color: #495057;
    transition: all 0.2s;
}
.btn-cancel:hover {
    background: #f1f3f5;
    border-color: #adb5bd;
}
.btn-save {
    background: #00bcd4;
    color: white;
    border: none;
    border-radius: 6px;
    padding: 8px 20px;
    font-weight: 600;
    cursor: pointer;
    transition: background-color 0.2s;
}
.btn-save:hover {
    background: #00a5bb;
}
@keyframes fadeIn {
    from { opacity: 0; transform: scale(0.95); }
    to { opacity: 1; transform: scale(1); }
}
</style>